/**
 * DataScient colour tokens, one set per theme.
 *
 * The CSS custom properties in styles.css are the source the DOM reads (Tailwind
 * classes and `sx` values via `dsVar`). These hex values are their twin for the
 * places CSS variables cannot reach: canvas/SVG libraries (cytoscape, recharts,
 * sankey) and MUI's palette, whose colour helpers cannot parse `var(...)`.
 * tokens.spec.ts keeps the two in step.
 */

export type ThemeName = 'dark' | 'light';

export type TokenName =
    | 'page'
    | 'surface'
    | 'raised'
    | 'line'
    | 'fg'
    | 'fg-body'
    | 'fg-soft'
    | 'fg-2'
    | 'fg-muted'
    | 'fg-faint'
    | 'accent'
    | 'on-accent'
    | 'accent-fg'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'warning-soft'
    | 'warning-line'
    | 'subflow';

export type ThemeTokens = Record<TokenName, string>;

export const darkTokens: ThemeTokens = {
    page: '#0A192F',
    surface: '#112240',
    raised: '#1D2D44',
    line: '#233554',
    fg: '#FFFFFF',
    'fg-body': '#E2E8F0',
    'fg-soft': '#CBD5E1',
    'fg-2': '#94A3B8',
    'fg-muted': '#64748B',
    'fg-faint': '#475569',
    accent: '#FFDE59',
    'on-accent': '#0A192F',
    'accent-fg': '#FFDE59',
    success: '#10B981',
    danger: '#EF4444',
    warning: '#FBBF24',
    info: '#60A5FA',
    // Caution panels: an amber wash and its edge.
    'warning-soft': '#221B0C',
    'warning-line': '#3A2F16',
    // The flow editor's canvas while editing a sub-flow.
    subflow: '#13102A',
};

// Brand yellow stays a fill on light; as text it is 1.3:1 on white, so anything
// that draws yellow *as text* uses accent-fg, a dark gold that clears 4.5:1.
export const lightTokens: ThemeTokens = {
    page: '#F5F7FA',
    surface: '#FFFFFF',
    raised: '#EEF2F7',
    line: '#D8DFE9',
    fg: '#0A192F',
    'fg-body': '#1E293B',
    'fg-soft': '#334155',
    'fg-2': '#475569',
    'fg-muted': '#64748B',
    'fg-faint': '#94A3B8',
    accent: '#FFDE59',
    'on-accent': '#0A192F',
    'accent-fg': '#8A6A00',
    success: '#047857',
    danger: '#B91C1C',
    warning: '#B45309',
    info: '#1D4ED8',
    'warning-soft': '#FFFBEB',
    'warning-line': '#FDE68A',
    subflow: '#F5F3FF',
};

export const tokensFor = (theme: ThemeName): ThemeTokens =>
    theme === 'light' ? lightTokens : darkTokens;

/**
 * A token as a CSS value for `sx`/inline styles, following the active theme.
 * `alpha` gives the translucent form, e.g. dsVar('accent', 0.1) for a tinted fill.
 */
export const dsVar = (name: TokenName, alpha?: number): string =>
    alpha === undefined ? `rgb(var(--ds-${name}))` : `rgb(var(--ds-${name}) / ${alpha})`;

export const FONT_BODY = '"Inter", sans-serif';
export const FONT_MONO = '"JetBrains Mono", monospace';

/**
 * A status or category hue (tier, sentiment, review state) as legible text in
 * either theme: unchanged on dark, darkened on light where the raw hue — amber
 * especially — would wash out on white. Strength is --ds-ink-mix in styles.css.
 */
export const ink = (hex: string): string => `color-mix(in srgb, ${hex} var(--ds-ink-mix), black)`;
